import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { client } from "../../..";
import { WA_CONTACT_CONSENT, WA_START_CONVERSATION } from "../../../Queries/WhatsApp";
import { setWaActive, upsertWaConversation, upsertWaMessage, fetchWaStatus } from "../../../Slices/whatsappSlice";
import SidePanel from "./SidePanel";
import RecordSearch from "./RecordSearch";
import ConsentForm from "./ConsentForm";
import TemplateComposer from "./TemplateComposer";
import { sendErrorText, gqlErrorMessage } from "./waUtils";

/**
 * Outbound-first contact: pick a lead/client contact (or type a number), make
 * sure consent exists, choose a template, send. One recipient at a time —
 * bulk sending is intentionally not possible.
 *
 * initial (optional): { phone, contactName, leadId, leadContactId, clientId, recordName }
 */
const NewConversationPanel = ({ onClose, initial, onStarted }) => {
  const dispatch = useDispatch();
  const senderName = useSelector((s) => s.whatsapp.status?.userName);
  const [step, setStep] = useState(initial?.phone ? 2 : 1);
  const [manual, setManual] = useState(false);
  const [recipient, setRecipient] = useState(
    initial?.phone
      ? {
          phone: initial.phone,
          contactName: initial.contactName || "",
          recordName: initial.recordName || "",
          leadId: initial.leadId || null,
          leadContactId: initial.leadContactId || null,
          clientId: initial.clientId || null,
        }
      : null
  );
  const [manualPhone, setManualPhone] = useState("");
  const [manualName, setManualName] = useState("");
  const [consent, setConsent] = useState(null);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentError, setConsentError] = useState(null);
  const [tpl, setTpl] = useState({ template: null, values: [], valid: false });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const onTplChange = useCallback((s) => setTpl(s), []);

  const loadConsent = useCallback(async (phone) => {
    setConsentLoading(true);
    setConsentError(null);
    try {
      const { data } = await client.query({ query: WA_CONTACT_CONSENT, variables: { phone }, fetchPolicy: "network-only" });
      setConsent(data.whatsappContactConsent);
    } catch (err) {
      setConsentError(gqlErrorMessage(err));
    } finally {
      setConsentLoading(false);
    }
  }, []);

  useEffect(() => {
    if (recipient?.phone) loadConsent(recipient.phone);
    else setConsent(null);
  }, [recipient?.phone, loadConsent]);

  const pickRecord = ({ kind, id, title, contact }) => {
    setRecipient({
      phone: contact.phone,
      contactName: contact.name || "",
      recordName: title,
      leadId: kind === "lead" ? id : null,
      leadContactId: kind === "lead" ? contact.id : null,
      clientId: kind === "client" ? id : null,
    });
  };

  const useManual = () => {
    setRecipient({ phone: manualPhone.trim(), contactName: manualName.trim(), recordName: "", leadId: null, leadContactId: null, clientId: null });
  };

  const consentOk = consent?.valid && consent.optInStatus !== "opted_out" && (consent.optInStatus === "opted_in" || consent.hasPriorInbound);

  const send = async () => {
    if (!tpl.valid || sending || !recipient) return;
    setSending(true);
    setError(null);
    try {
      const { data } = await client.mutate({
        mutation: WA_START_CONVERSATION,
        variables: {
          phone: recipient.phone,
          contactName: recipient.contactName || null,
          leadId: recipient.leadId,
          leadContactId: recipient.leadContactId,
          clientId: recipient.clientId,
          templateName: tpl.template.name,
          language: tpl.template.language,
          params: tpl.values.map((v) => String(v).trim()),
        },
      });
      const res = data.whatsappStartConversation;
      if (!res.ok) {
        setError({ code: res.code, message: res.error });
        return;
      }
      if (res.conversation) {
        dispatch(upsertWaConversation(res.conversation));
        dispatch(setWaActive(res.conversation.id));
      }
      if (res.message) dispatch(upsertWaMessage(res.message));
      dispatch(fetchWaStatus());
      onStarted?.(res.conversation);
      onClose();
    } catch (err) {
      setError({ message: gqlErrorMessage(err) });
    } finally {
      setSending(false);
    }
  };

  const footer =
    step === 1 ? (
      <>
        <span className="wa-help">One contact at a time — WhatsApp does not allow bulk messaging from the inbox.</span>
        <button type="button" className="wa-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="wa-btn wa-btn--primary" disabled={!recipient || !consentOk} onClick={() => setStep(2)}>
          Next: choose template <i className="fa-solid fa-arrow-right" />
        </button>
      </>
    ) : (
      <>
        {error ? (
          <span className="wa-help" style={{ color: "var(--wa-danger)" }} role="alert">
            {sendErrorText(error)}
          </span>
        ) : <span className="wa-help"><span className="wa-kbd">Ctrl</span>+<span className="wa-kbd">Enter</span> to send</span>}
        {!initial?.phone && <button type="button" className="wa-btn" onClick={() => setStep(1)}><i className="fa-solid fa-arrow-left" /> Back</button>}
        <button type="button" className="wa-btn wa-btn--primary" disabled={!tpl.valid || sending || !consentOk} onClick={send}>
          {sending ? <i className="fa-solid fa-spinner wa-spin" /> : <i className="fa-solid fa-paper-plane" />} Send
        </button>
      </>
    );

  return (
    <SidePanel wide title="New WhatsApp message" subtitle="Start a conversation with an approved template" onClose={onClose} footer={footer}>
      <div className="wa-steps">
        <div className={`wa-step ${step === 1 ? "is-active" : "is-done"}`}><b>1</b> Recipient & consent</div>
        <div className={`wa-step ${step === 2 ? "is-active" : ""}`}><b>2</b> Template</div>
      </div>

      {recipient && (
        <div className="wa-block" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <i className="fa-brands fa-whatsapp" style={{ fontSize: 22, color: "var(--wa-accent)" }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <b>{recipient.contactName || "New contact"}</b>
            <div className="wa-help" style={{ margin: 0 }}>
              {consent?.displayNumber || recipient.phone}{recipient.recordName ? ` · ${recipient.recordName}` : ""}
            </div>
          </div>
          {consentLoading ? (
            <span className="wa-chip"><i className="fa-solid fa-spinner wa-spin" /> Checking</span>
          ) : consent && !consent.valid ? (
            <span className="wa-chip wa-chip--danger">Invalid number</span>
          ) : consent?.optInStatus === "opted_out" ? (
            <span className="wa-chip wa-chip--danger">Opted out</span>
          ) : consent?.optInStatus === "opted_in" ? (
            <span className="wa-chip wa-chip--ok"><i className="fa-solid fa-user-check" /> Opted in</span>
          ) : consent?.hasPriorInbound ? (
            <span className="wa-chip wa-chip--accent">Has messaged us</span>
          ) : consent ? (
            <span className="wa-chip wa-chip--warn">No consent yet</span>
          ) : null}
          {step === 1 && !initial?.phone && (
            <button type="button" className="wa-btn wa-btn--sm" onClick={() => { setRecipient(null); setConsent(null); }}>Change</button>
          )}
        </div>
      )}

      {consentError && <div className="wa-alert wa-alert--danger" style={{ marginBottom: 12 }}>{consentError}</div>}
      {consent?.conversationId && (
        <div className="wa-alert wa-alert--info" style={{ marginBottom: 12 }}>
          <i className="fa-solid fa-circle-info" /> A conversation with this number already exists — the template will be added to it.
        </div>
      )}
      {consent?.valid && consent.optInStatus === "opted_out" && (
        <div className="wa-alert wa-alert--danger" style={{ marginBottom: 12 }}>
          <i className="fa-solid fa-ban" /> This contact replied STOP. They have to message START before anyone can contact them on WhatsApp.
        </div>
      )}
      {consent?.valid && consent.optInStatus === "unknown" && !consent.hasPriorInbound && (
        <div style={{ marginBottom: 12 }}>
          <div className="wa-alert wa-alert--warn" style={{ marginBottom: 10 }}>
            <i className="fa-solid fa-user-shield" /> WhatsApp requires the contact's permission before we message them first. Record how they agreed:
          </div>
          <ConsentForm phone={recipient.phone} leadId={recipient.leadId} onSaved={() => loadConsent(recipient.phone)} />
        </div>
      )}

      {step === 1 && !recipient && (
        <>
          {!manual ? (
            <>
              <RecordSearch onPick={pickRecord} />
              <button type="button" className="wa-btn wa-btn--ghost wa-btn--sm" onClick={() => setManual(true)}>
                <i className="fa-solid fa-keyboard" /> Enter a number instead
              </button>
            </>
          ) : (
            <div>
              <div className="wa-field">
                <label className="wa-label" htmlFor="wa-new-phone">WhatsApp number</label>
                <input id="wa-new-phone" className="wa-input" value={manualPhone} onChange={(e) => setManualPhone(e.target.value)} placeholder="+1 302 555 1234" autoFocus />
                <div className="wa-help">US numbers can omit +1. Include the country code for other countries.</div>
              </div>
              <div className="wa-field">
                <label className="wa-label" htmlFor="wa-new-name">Contact name</label>
                <input id="wa-new-name" className="wa-input" value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Jane Doe" />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" className="wa-btn" onClick={() => setManual(false)}>Search records instead</button>
                <button type="button" className="wa-btn wa-btn--primary" disabled={manualPhone.replace(/\D/g, "").length < 10} onClick={useManual}>Use this number</button>
              </div>
            </div>
          )}
        </>
      )}

      {step === 2 && recipient && (
        <div onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); } }}>
          <TemplateComposer
            fillContext={{ contactName: recipient.contactName, recordName: recipient.recordName, senderName }}
            compliance={consent ? { optInStatus: consent.optInStatus, hasPriorInbound: consent.hasPriorInbound } : null}
            onChange={onTplChange}
          />
        </div>
      )}
    </SidePanel>
  );
};

export default NewConversationPanel;
